// Change a placed lint pot (owner, 2026-10-19): the chamber diameter and each end (inlet or outlet, size, angle,
// length); add an end; take an end away. The geometry is in src/library/lintPots.ts.
//
// The pot keeps its group id, so every pipe on it stays attached: a pipe is attached to an end by its INDEX, and the
// ends keep their order. When an end is taken away, the pipe on it is set free where it is (its end is cut flat) and
// the pipes on the later ends are moved down by one index. When an end turns, gets longer or the chamber gets
// larger, the end moves: a pipe on it whose far end is free moves with it, and keeps its length and its angle to the
// end (src/pipes/api.ts). With "pipes take the new size" on, the pipes on an end whose size changes, and the pipes
// joined to them, take the new size, up to the next fitting or pump.
//
// The pot is redrawn with new parts in ONE command (ungroup, old parts out, new parts in, the same group around them),
// so the number of ends can change. One undo puts it all back.
import type { Vec } from "../core/geometry";
import type { Doc, Entity, Style } from "../doc/entities";
import { applyCommand, makeDeleteCommand, makeUngroupCommand, type Command } from "../doc/commands";
import { newId } from "../doc/document";
import { CENTRE, OUTLINE } from "../library/fittings";
import {
  defaultEndLength,
  lintPotEntities,
  lintPotFitting,
  lintPotParts,
  lintPotProblem,
  normAngle,
  type EndRole,
  type LintPotEnd,
  type LintPotSpec
} from "../library/lintPots";
import { pipeMetaOf, setPipePatches, type Patch, type PipeMeta } from "./api";
import { pipeRefOf, rawPorts, type PipeRef } from "./carry";
import { runFrom } from "./resizeFitting";

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const r6 = (v: number) => Math.round(v * 1e6) / 1e6;

export const CHANGED_POT = "This lint pot was changed after it was placed (scaled, mirrored or ungrouped). Place a new lint pot.";

/** meta.lintpot of a lint pot group, when it is well formed. Angles in the pot's own frame. */
export function lintPotSpecOf(entity: Entity | undefined): LintPotSpec | null {
  if (!entity || entity.type !== "group") return null;
  if ((entity.meta?.fitting as { kind?: unknown } | undefined)?.kind !== "lintpot") return null;
  const m = entity.meta?.lintpot as Record<string, unknown> | undefined;
  if (!m || !num(m.diameter) || !Array.isArray(m.ends) || m.ends.length === 0) return null;
  const ends: LintPotEnd[] = [];
  for (const raw of m.ends as unknown[]) {
    const e = raw as Record<string, unknown> | null;
    if (!e || (e.role !== "inlet" && e.role !== "outlet") || !num(e.size) || !num(e.angle) || !num(e.length)) return null;
    // 3.16.1: a bend on the end (the direction its face looks and the length after the bend)
    const b = e.bend as Record<string, unknown> | null | undefined;
    const bend = b && num(b.angle) && num(b.length) ? { bend: { angle: b.angle, length: b.length } } : {};
    ends.push({ role: e.role as EndRole, size: e.size, angle: e.angle, length: e.length, ...(num(e.height) ? { height: e.height } : {}), ...bend });
  }
  // 3.16.0: the height, the make (JagPoly), the model and the view (plan or elevation)
  return {
    diameter: m.diameter,
    ends,
    ...(num(m.height) ? { height: m.height } : {}),
    ...(m.make === "jagpoly" ? { make: "jagpoly" as const } : {}),
    ...(typeof m.model === "string" && m.model ? { model: m.model } : {}),
    ...(m.view === "elevation" ? { view: "elevation" as const } : {}),
    // 3.31.0: the side an elevation is seen from (absent = the front)
    ...(m.view === "elevation" && num(m.seenFrom) && normAngle(m.seenFrom) !== 90 ? { seenFrom: normAngle(m.seenFrom) } : {})
  };
}

export const isLintPot = (entity: Entity | undefined): boolean => lintPotSpecOf(entity) !== null;

/**
 * The pot as it stands on the drawing: its values, its centre (plan) or the centre of its base (elevation), and the turn
 * of the picture. A PLAN gives its angles AS DRAWN (its turn added) and turn 0. An ELEVATION keeps its angles (they are
 * the plan directions) and gives the turn of its picture. The turn and the centre are read back from end 1 against the
 * pot drawn in its own frame; every other end must then be where the values put it, else the pot was scaled, mirrored
 * or edited, and a sentence is returned.
 */
export function placedLintPot(doc: Doc, id: string): { spec: LintPotSpec; centre: Vec; turn: number } | string {
  const g = doc.entities[id];
  const own = lintPotSpecOf(g);
  if (!g || !own) return "Select a lint pot.";
  const ports = rawPorts(g);
  if (!ports || ports.length !== own.ends.length) return CHANGED_POT;
  const p0 = ports[0]!;
  const local = lintPotParts(own, { x: 0, y: 0 }, 0).ports[0]!;
  const turn = ((Math.atan2(p0.dir.y, p0.dir.x) - Math.atan2(local.dir.y, local.dir.x)) * 180) / Math.PI;
  const c = Math.cos((turn * Math.PI) / 180);
  const sn = Math.sin((turn * Math.PI) / 180);
  const centre = { x: p0.p.x - (local.p.x * c - local.p.y * sn), y: p0.p.y - (local.p.x * sn + local.p.y * c) };
  const elevation = own.view === "elevation";
  const spec: LintPotSpec = elevation
    ? own
    : { ...own, ends: own.ends.map((e) => ({ ...e, angle: normAngle(r6(e.angle + turn)), ...(e.bend ? { bend: { ...e.bend, angle: normAngle(r6(e.bend.angle + turn)) } } : {}) })) };
  const pictureTurn = elevation ? normAngle(r6(turn)) : 0;
  const want = lintPotParts(spec, centre, pictureTurn).ports;
  for (let i = 0; i < ports.length; i++) {
    const a = ports[i]!;
    const b = want[i]!;
    if (Math.hypot(a.p.x - b.p.x, a.p.y - b.p.y) > 0.01 || Math.hypot(a.dir.x - b.dir.x, a.dir.y - b.dir.y) > 1e-6 || Math.abs(a.size - b.size) > 1e-9) {
      return CHANGED_POT;
    }
  }
  return { spec, centre: { x: r6(centre.x), y: r6(centre.y) }, turn: pictureTurn };
}

/** The values with one more end: an outlet the size of the smallest outlet, in the middle of the largest space. */
export function withAnotherEnd(spec: LintPotSpec): LintPotSpec | string {
  const outlets = spec.ends.filter((e) => e.role === "outlet");
  const size = outlets.length ? Math.min(...outlets.map((e) => e.size)) : spec.ends[spec.ends.length - 1]!.size;
  const angles = spec.ends.map((e) => normAngle(e.angle)).sort((a, b) => a - b);
  let best = { from: angles[0]!, gap: 360 };
  if (angles.length > 1) {
    best = { from: angles[angles.length - 1]!, gap: angles[0]! + 360 - angles[angles.length - 1]! };
    for (let i = 1; i < angles.length; i++) {
      const gap = angles[i]! - angles[i - 1]!;
      if (gap > best.gap) best = { from: angles[i - 1]!, gap };
    }
  }
  const mid = best.from + best.gap / 2;
  for (const angle of [normAngle(Math.round(mid)), normAngle(r6(mid))]) {
    const next: LintPotSpec = { ...spec, ends: [...spec.ends, { role: "outlet", size, angle, length: defaultEndLength(size, spec) }] };
    if (!lintPotProblem(next)) return next;
  }
  return "There is no space for another end. Make the chamber larger or the ends smaller.";
}

export interface LintPotEdit {
  /** The next values, angles AS DRAWN. */
  spec: LintPotSpec;
  /** The index of an end that is taken away. The ends after it move down by one. */
  removed?: number;
  /** On (the default): the pipes on an end whose size changes, and the pipes joined to them, take the new size. */
  withPipes?: boolean;
  /** Draw the lines again from the values even when the values do not change (a line was moved by hand). */
  force?: boolean;
  /** 3.16.1: the style. Absent: the pot keeps its style. "jagpoly": the JagPoly lid ring and flanges. null: plain. */
  make?: "jagpoly" | null;
}

const styleOf = (e: Entity | undefined): Style | undefined => (e && "style" in e ? (e.style as Style) : undefined);

/** ONE command that redraws lint pot `id` with `edit.spec`; a sentence instead when it cannot. */
export function lintPotCommand(doc: Doc, id: string, edit: LintPotEdit): { cmd: Command; pipesChanged: number; pipesFreed: number } | string {
  const g = doc.entities[id];
  const cur = placedLintPot(doc, id);
  if (typeof cur === "string") return cur;
  if (!g || g.type !== "group") return "Select a lint pot.";
  // the values as given, with the angles tidied; the model and the view stay what the pot is, the make too unless the
  // edit gives a style (a new style drops the model). 3.31.0: an elevation takes the side it is seen from from the edit.
  const seenFrom = normAngle(edit.spec.seenFrom ?? cur.spec.seenFrom ?? 90);
  // 3.31.0: the keys in the order lintPotSpecOf gives them, so the "already has these values" test below can compare
  // the text (before, a pot with a height was never found unchanged: its keys came in another order)
  const next: LintPotSpec = {
    diameter: edit.spec.diameter,
    ends: edit.spec.ends.map((e) => ({
      role: e.role,
      size: e.size,
      angle: normAngle(r6(e.angle)),
      length: e.length,
      ...(e.height !== undefined ? { height: e.height } : {}),
      ...(e.bend ? { bend: { angle: normAngle(r6(e.bend.angle)), length: e.bend.length } } : {})
    })),
    ...(edit.spec.height !== undefined ? { height: edit.spec.height } : {}),
    ...(edit.make === undefined ? (cur.spec.make ? { make: cur.spec.make } : {}) : edit.make ? { make: edit.make } : {}),
    ...(edit.make === undefined && cur.spec.model ? { model: cur.spec.model } : {}),
    ...(cur.spec.view ? { view: cur.spec.view } : {}),
    ...(cur.spec.view === "elevation" && seenFrom !== 90 ? { seenFrom } : {})
  };
  const problem = lintPotProblem(next);
  if (problem) return problem;
  const removed = edit.removed;
  if (removed !== undefined) {
    if (!Number.isInteger(removed) || removed < 0 || removed >= cur.spec.ends.length) return "That end is not on this lint pot.";
    if (next.ends.length !== cur.spec.ends.length - 1) return "Take away one end at a time.";
  } else if (next.ends.length < cur.spec.ends.length) return "Take away one end at a time.";
  if (removed === undefined && !edit.force && JSON.stringify(next) === JSON.stringify(cur.spec)) return "The lint pot already has these values.";
  /** old end index → new end index (null: taken away) */
  const newIndex = (old: number): number | null => (removed === undefined ? old : old === removed ? null : old > removed ? old - 1 : old);
  /** new end index → old end index (null: a new end) */
  const oldIndex = (ni: number): number | null => {
    const oi = removed === undefined ? ni : ni >= removed ? ni + 1 : ni;
    return oi < cur.spec.ends.length ? oi : null;
  };

  const commands: Command[] = [];

  // 1. pipes on an end that is taken away are set free; pipes on later ends move down by one index
  const ports = rawPorts(g)!;
  const anchorPatches: Patch[] = [];
  let pipesFreed = 0;
  if (removed !== undefined) {
    for (const e of Object.values(doc.entities)) {
      const pm = pipeMetaOf(e);
      if (!pm) continue;
      const nextPm: PipeMeta = structuredClone(pm);
      const nextRef: PipeRef = structuredClone(pipeRefOf(e));
      let changed = false;
      for (const k of ["a", "b"] as const) {
        const an = nextPm[k];
        if (an.kind !== "port" || an.ownerId !== id) continue;
        const ni = newIndex(an.port);
        if (ni === an.port) continue;
        changed = true;
        if (ni === null) {
          const at = ports[an.port]!.p;
          nextPm[k] = { kind: "free", p: { x: at.x, y: at.y } };
          if (k === "a") nextPm.endA = "flat";
          else nextPm.endB = "flat";
          delete nextRef[k];
          pipesFreed++;
        } else {
          nextPm[k] = { ...an, port: ni };
          const r = nextRef[k];
          if (r && r.ownerId === id) nextRef[k] = { ...r, port: ni };
        }
      }
      if (!changed) continue;
      const meta: Record<string, unknown> = { ...(e.meta ?? {}), pipe: nextPm };
      if (nextRef.a || nextRef.b) meta.pipeRef = nextRef;
      else delete meta.pipeRef;
      anchorPatches.push({ id: e.id, before: { meta: structuredClone(e.meta) }, after: { meta } });
    }
  }
  if (anchorPatches.length) commands.push({ kind: "updateEntities", patches: anchorPatches });

  // 2. the pot: old parts out, new parts in, the same group (same id, same place in the layer) around them.
  //    The parts keep the colour and line the person gave them.
  const kids = g.children.map((c) => doc.entities[c]).filter((e): e is Entity => !!e);
  // a solid line first: an elevation also has dashed (hidden) lines
  const outline = kids.map(styleOf).find((s) => s && s.dash === "solid") ?? kids.map(styleOf).find((s) => s && s.dash !== "dashdot") ?? OUTLINE;
  const centre = kids.map(styleOf).find((s) => s && s.dash === "dashdot") ?? CENTRE;
  const layerId = kids[0]?.layerId ?? g.layerId;
  const made = lintPotEntities(next, newId, layerId, cur.centre, cur.turn, { outline, centre });
  const groupZ = { layerId: g.layerId, index: (doc.zOrder[g.layerId] ?? []).indexOf(id) };
  // In a batch of its own: the app reports a delete when a command holds a delete step at its top level, and this is
  // a redraw, not a delete.
  const redraw: Command[] = [makeUngroupCommand(doc, id)];
  if (g.children.length) redraw.push(makeDeleteCommand(doc, [...g.children]));
  redraw.push({ kind: "addEntities", entities: made.entities, z: made.entities.map(() => ({ layerId, index: -1 })) });
  const pivot = (g.meta?.pivot as Vec | undefined) ?? cur.centre;
  const meta = { ...(g.meta ?? {}), fitting: lintPotFitting(next), lintpot: next, ports: made.ports, pivot };
  redraw.push({
    kind: "group",
    group: { ...structuredClone(g), children: made.entities.map((e) => e.id), meta },
    z: groupZ,
    children: made.entities.map((e) => ({ id: e.id, z: { layerId, index: -1 } }))
  });
  commands.push({ kind: "batch", label: "redraw lint pot", commands: redraw });

  // 3. pipes on an end whose size changed take the new size, with the pipes joined to them
  let pipesChanged = 0;
  if (edit.withPipes !== false) {
    const draft = structuredClone(doc);
    for (const c of commands) applyCommand(draft, c);
    const done = new Set<string>();
    next.ends.forEach((end, ni) => {
      const oi = oldIndex(ni);
      if (oi === null || cur.spec.ends[oi]!.size === end.size) return;
      for (const e of Object.values(draft.entities)) {
        const pm = pipeMetaOf(e);
        if (!pm || done.has(e.id)) continue;
        if (![pm.a, pm.b].some((a) => a.kind === "port" && a.ownerId === id && a.port === ni)) continue;
        for (const pid of runFrom(draft, e.id)) {
          if (done.has(pid)) continue;
          done.add(pid);
          const pmNow = pipeMetaOf(draft.entities[pid]);
          if (!pmNow || pmNow.size === end.size) continue;
          const ps = setPipePatches(draft, pid, { size: end.size });
          if (!ps.length) continue;
          const cmd: Command = { kind: "updateEntities", patches: ps };
          applyCommand(draft, cmd);
          commands.push(cmd);
          pipesChanged++;
        }
      }
    });
  }
  return { cmd: { kind: "batch", label: "lint pot", commands }, pipesChanged, pipesFreed };
}

/**
 * Load repair (2026-10-20): a lint pot whose parts are missing gets its parts drawn again from its values (meta.lintpot
 * and its ends, meta.ports), in place, with new ids on the layer of the pot. False when the pot cannot be read.
 * Cause seen on DP-PL-001: the parts were grouped a second time (Ctrl+G), and that second group was deleted.
 */
export function rebuildLintPotPartsInPlace(doc: Doc, id: string): boolean {
  const g = doc.entities[id];
  if (!g || g.type !== "group") return false;
  const placed = placedLintPot(doc, id);
  if (typeof placed === "string") return false;
  for (const c of g.children) {
    const e = doc.entities[c];
    if (e && e.type !== "group") delete doc.entities[c];
  }
  const made = lintPotEntities(placed.spec, newId, g.layerId, placed.centre, placed.turn);
  for (const e of made.entities) doc.entities[e.id] = e;
  g.children = made.entities.map((e) => e.id);
  g.meta = { ...(g.meta ?? {}), lintpot: placed.spec, ports: made.ports };
  return true;
}
